import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useKeycloak } from "@react-keycloak/web";
import { UNSAFE_NavigationContext, useBeforeUnload } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import uuid from "react-uuid";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import DualListBox from "../../../common/components/form/dualListBox";
import Loading from "../../../common/components/loading/loading";
import { getOrganizations } from "../../../common/services/auth/organizationService";
import { getAssociatedTradingOrganizations } from "../../../common/services/tradingEngine/marketStateService";
import {
  getCompanyWhitelists,
  getDelegatedCompanyWhitelists,
  saveCompanyWhitelist,
  saveDelegatedCompanyWhitelist,
} from "../../../common/services/tradingEngine/companyWhitelistService";

const createDefaultPolicy = (organizationId = "") => ({
  organizationId,
  buyFromAll: false,
  sellToAll: false,
  active: true,
  counterparties: [],
});

const normalizePolicy = (policy) => ({
  organizationId: policy.organizationId,
  buyFromAll: !!policy.buyFromAll,
  sellToAll: !!policy.sellToAll,
  active: policy.active !== false,
  counterparties: (policy.counterparties || [])
    .map((counterparty) => ({
      counterpartyOrganizationId: counterparty.counterpartyOrganizationId,
      canBuyFrom: !!counterparty.canBuyFrom,
      canSellTo: !!counterparty.canSellTo,
    }))
    .filter(
      (counterparty) =>
        counterparty.counterpartyOrganizationId &&
        (counterparty.canBuyFrom || counterparty.canSellTo)
    )
    .sort((a, b) =>
      a.counterpartyOrganizationId.localeCompare(b.counterpartyOrganizationId)
    ),
});

const buildOrganizationCatalog = (
  organizations,
  policiesByOrganizationId,
  additionalOrganizationIds = []
) => {
  const organizationMap = new Map();

  (organizations || []).forEach((organization) => {
    if (!organization?.organizationId) {
      return;
    }

    organizationMap.set(organization.organizationId, {
      organizationId: organization.organizationId,
      name:
        organization.name || organization.shortName || organization.organizationId,
      shortName: organization.shortName || "",
      active: organization.active !== false,
    });
  });

  additionalOrganizationIds.forEach((organizationId) => {
    if (organizationId && !organizationMap.has(organizationId)) {
      organizationMap.set(organizationId, {
        organizationId,
        name: organizationId,
        shortName: "",
        active: true,
      });
    }
  });

  Object.values(policiesByOrganizationId).forEach((policy) => {
    if (policy.organizationId && !organizationMap.has(policy.organizationId)) {
      organizationMap.set(policy.organizationId, {
        organizationId: policy.organizationId,
        name: policy.organizationId,
        shortName: "",
        active: true,
      });
    }

    (policy.counterparties || []).forEach((counterparty) => {
      if (
        counterparty.counterpartyOrganizationId &&
        !organizationMap.has(counterparty.counterpartyOrganizationId)
      ) {
        organizationMap.set(counterparty.counterpartyOrganizationId, {
          organizationId: counterparty.counterpartyOrganizationId,
          name: counterparty.counterpartyOrganizationId,
          shortName: "",
          active: true,
        });
      }
    });
  });

  return Array.from(organizationMap.values()).sort((a, b) =>
    (a.name || a.organizationId).localeCompare(b.name || b.organizationId)
  );
};

const toPolicyMap = (policies) =>
  (policies || []).reduce((accumulator, policy) => {
    accumulator[policy.organizationId] = normalizePolicy(policy);
    return accumulator;
  }, {});

const toRequestModel = (policy) => ({
  organizationId: policy.organizationId,
  buyFromAll: !!policy.buyFromAll,
  sellToAll: !!policy.sellToAll,
  active: policy.active !== false,
  eventId: uuid(),
  counterparties: (policy.counterparties || []).map((counterparty) => ({
    counterpartyOrganizationId: counterparty.counterpartyOrganizationId,
    canBuyFrom: !!counterparty.canBuyFrom,
    canSellTo: !!counterparty.canSellTo,
    active: counterparty.canBuyFrom || counterparty.canSellTo,
  })),
});

const arePoliciesEqual = (leftPolicy, rightPolicy) =>
  JSON.stringify(normalizePolicy(leftPolicy)) ===
  JSON.stringify(normalizePolicy(rightPolicy));

const CompanyWhitelistManager = ({ delegated = false, allowEdit = false }) => {
  const { t } = useTranslation(["trading", "common"]);
  const { keycloak } = useKeycloak();
  const navigationContext = useContext(UNSAFE_NavigationContext);
  const historyNavigator = navigationContext?.navigator;
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [organizationCatalog, setOrganizationCatalog] = useState([]);
  const [policiesByOrganizationId, setPoliciesByOrganizationId] = useState({});
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [editorPolicy, setEditorPolicy] = useState(createDefaultPolicy());
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const pendingActionRef = useRef(null);
  const unblockNavigationRef = useRef(null);

  const delegatedOrganizationIds = useMemo(() => {
    const rawGroups = [
      keycloak?.tokenParsed?.["http://schemas.xmlsoap.org/claims/Group"],
      keycloak?.tokenParsed?.groups,
      keycloak?.tokenParsed?.group,
    ].flatMap((value) => {
      if (Array.isArray(value)) {
        return value;
      }

      return value ? [value] : [];
    });

    return Array.from(new Set(rawGroups)).filter((value) =>
      /^[0-9a-fA-F-]{36}$/.test(value)
    );
  }, [keycloak]);

  const manageableOrganizations = useMemo(() => {
    const permittedOrganizationIds = new Set(
      delegated
        ? delegatedOrganizationIds.length > 0
          ? delegatedOrganizationIds
          : Object.keys(policiesByOrganizationId)
        : organizationCatalog.map((organization) => organization.organizationId)
    );

    return organizationCatalog
      .filter((organization) =>
        permittedOrganizationIds.has(organization.organizationId)
      )
      .filter((organization) => {
        if (!searchQuery) {
          return true;
        }

        const lookup = `${organization.name || ""} ${organization.shortName || ""} ${organization.organizationId}`.toLowerCase();
        return lookup.includes(searchQuery.toLowerCase());
      });
  }, [
    delegated,
    delegatedOrganizationIds,
    organizationCatalog,
    policiesByOrganizationId,
    searchQuery,
  ]);

  const currentBuyFromSelection = useMemo(
    () =>
      editorPolicy.counterparties
        .filter((counterparty) => counterparty.canBuyFrom)
        .map((counterparty) => counterparty.counterpartyOrganizationId),
    [editorPolicy]
  );

  const currentSellToSelection = useMemo(
    () =>
      editorPolicy.counterparties
        .filter((counterparty) => counterparty.canSellTo)
        .map((counterparty) => counterparty.counterpartyOrganizationId),
    [editorPolicy]
  );

  const counterpartyOptions = useMemo(() => {
    const selectedCounterpartyIds = new Set([
      ...currentBuyFromSelection,
      ...currentSellToSelection,
    ]);

    return organizationCatalog
      .filter(
        (organization) => organization.organizationId !== selectedOrganizationId
      )
      .map((organization) => ({
        value: organization.organizationId,
        label:
          organization.shortName && organization.shortName !== organization.name
            ? `${organization.name} (${organization.shortName})`
            : organization.name || organization.organizationId,
      }))
      .concat(
        Array.from(selectedCounterpartyIds)
          .filter(
            (organizationId) =>
              !organizationCatalog.some(
                (organization) => organization.organizationId === organizationId
              )
          )
          .map((organizationId) => ({
            value: organizationId,
            label: organizationId,
          }))
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [
    organizationCatalog,
    selectedOrganizationId,
    currentBuyFromSelection,
    currentSellToSelection,
  ]);

  const selectedPolicy = useMemo(() => {
    if (!selectedOrganizationId) {
      return createDefaultPolicy();
    }

    return (
      policiesByOrganizationId[selectedOrganizationId] ||
      createDefaultPolicy(selectedOrganizationId)
    );
  }, [policiesByOrganizationId, selectedOrganizationId]);

  const hasUnsavedChanges =
    !!selectedOrganizationId &&
    allowEdit &&
    !arePoliciesEqual(editorPolicy, selectedPolicy);
  const unsavedChangesMessage = t("CompanyWhitelistUnsavedChangesWarning");

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!hasUnsavedChanges) {
          return;
        }

        event.preventDefault();
        event.returnValue = unsavedChangesMessage;
      },
      [hasUnsavedChanges, unsavedChangesMessage]
    )
  );

  useEffect(() => {
    if (!hasUnsavedChanges || typeof historyNavigator?.block !== "function") {
      if (typeof unblockNavigationRef.current === "function") {
        unblockNavigationRef.current();
        unblockNavigationRef.current = null;
      }

      return undefined;
    }

    if (typeof unblockNavigationRef.current === "function") {
      return undefined;
    }

    const unblock = historyNavigator.block((transition) => {
      pendingActionRef.current = () => {
        unblock();
        unblockNavigationRef.current = null;
        transition.retry();
      };
      setShowUnsavedChangesModal(true);
    });

    unblockNavigationRef.current = unblock;

    return () => {
      if (unblockNavigationRef.current === unblock) {
        unblock();
        unblockNavigationRef.current = null;
      }
    };
  }, [hasUnsavedChanges, historyNavigator]);

  const requestDiscardConfirmation = useCallback(
    (action) => {
      if (!hasUnsavedChanges) {
        action();
        return;
      }

      pendingActionRef.current = action;
      setShowUnsavedChangesModal(true);
    },
    [hasUnsavedChanges]
  );

  const handleCloseUnsavedChangesModal = useCallback(() => {
    pendingActionRef.current = null;
    setShowUnsavedChangesModal(false);
  }, []);

  const handleConfirmUnsavedChanges = useCallback(() => {
    const pendingAction = pendingActionRef.current;
    pendingActionRef.current = null;
    setShowUnsavedChangesModal(false);

    if (typeof pendingAction === "function") {
      pendingAction();
    }
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);

    try {
      const [organizations, policies, associatedOrganizationIds] = await Promise.all([
        getOrganizations().catch(() => []),
        delegated ? getDelegatedCompanyWhitelists() : getCompanyWhitelists(),
        getAssociatedTradingOrganizations().catch(() => []),
      ]);

      const associatedOrganizationIdSet = new Set(associatedOrganizationIds);
      const nextPoliciesByOrganizationId = toPolicyMap(policies);
      const nextOrganizationCatalog = buildOrganizationCatalog(
        (organizations || []).filter((organization) =>
          associatedOrganizationIdSet.has(organization.organizationId)
        ),
        nextPoliciesByOrganizationId,
        delegatedOrganizationIds.filter((organizationId) =>
          associatedOrganizationIdSet.has(organizationId)
        )
      ).filter((organization) =>
        associatedOrganizationIdSet.has(organization.organizationId)
      );

      setPoliciesByOrganizationId(nextPoliciesByOrganizationId);
      setOrganizationCatalog(nextOrganizationCatalog);

      const nextManageableOrganizations = nextOrganizationCatalog.filter(
        (organization) =>
          !delegated ||
          (delegatedOrganizationIds.length > 0
            ? delegatedOrganizationIds.includes(organization.organizationId)
            : Object.keys(nextPoliciesByOrganizationId).includes(
                organization.organizationId
              ))
      );

      setSelectedOrganizationId((currentSelectedOrganizationId) => {
        const currentSelectionIsValid = nextManageableOrganizations.some(
          (organization) =>
            organization.organizationId === currentSelectedOrganizationId
        );

        return currentSelectionIsValid
          ? currentSelectedOrganizationId
          : nextManageableOrganizations[0]?.organizationId || "";
      });
    } catch (error) {
      console.error("Failed to load company whitelists", error);
      toast.error(t("CompanyWhitelistLoadFailed"));
    } finally {
      setIsLoading(false);
    }
  }, [delegated, delegatedOrganizationIds, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedOrganizationId) {
      setEditorPolicy(createDefaultPolicy());
      return;
    }

    setEditorPolicy(normalizePolicy(selectedPolicy));
  }, [selectedOrganizationId, selectedPolicy]);

  const reloadData = async () => {
    requestDiscardConfirmation(() => {
      loadData();
    });
  };

  const updateEditorPolicy = (changes) => {
    if (!selectedOrganizationId || !allowEdit) {
      return;
    }

    setEditorPolicy((currentPolicy) =>
      normalizePolicy({
        ...currentPolicy,
        organizationId: selectedOrganizationId,
        ...changes,
      })
    );
  };

  const updateCounterpartyDirection = (direction, selectedIds) => {
    const buyFromIds = new Set(
      direction === "buy" ? selectedIds : currentBuyFromSelection
    );
    const sellToIds = new Set(
      direction === "sell" ? selectedIds : currentSellToSelection
    );
    const allIds = new Set([
      ...currentBuyFromSelection,
      ...currentSellToSelection,
      ...selectedIds,
    ]);

    const counterparties = Array.from(allIds)
      .filter(
        (organizationId) =>
          buyFromIds.has(organizationId) || sellToIds.has(organizationId)
      )
      .sort()
      .map((organizationId) => ({
        counterpartyOrganizationId: organizationId,
        canBuyFrom: buyFromIds.has(organizationId),
        canSellTo: sellToIds.has(organizationId),
      }));

    updateEditorPolicy({ counterparties });
  };

  const handleSaveCurrent = async () => {
    if (!selectedOrganizationId) {
      return;
    }

    const organizationId = selectedOrganizationId;
    const policyToSave = normalizePolicy({
      ...editorPolicy,
      organizationId,
    });

    setIsSaving(true);

    try {
      const savedPolicy = delegated
        ? await saveDelegatedCompanyWhitelist(
            organizationId,
            toRequestModel(policyToSave)
          )
        : await saveCompanyWhitelist(
            organizationId,
            toRequestModel(policyToSave)
          );

      const normalizedPolicy = normalizePolicy(savedPolicy);
      setPoliciesByOrganizationId((current) => ({
        ...current,
        [organizationId]: normalizedPolicy,
      }));
      setEditorPolicy(normalizedPolicy);
      toast.success(t("CompanyWhitelistSaved"));
    } catch (error) {
      console.error("Failed to save company whitelist", error);
      toast.error(t("CompanyWhitelistSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectOrganization = (organizationId) => {
    if (organizationId === selectedOrganizationId) {
      return;
    }

    requestDiscardConfirmation(() => {
      setSelectedOrganizationId(organizationId);
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="container-fluid">
      <Modal
        show={showUnsavedChangesModal}
        size="sm"
        onHide={handleCloseUnsavedChangesModal}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Body>
          <div className="row" style={{ textAlign: "center" }}>
            <p>{unsavedChangesMessage}</p>
          </div>
          <div className="row">
            <div className="col"></div>
            <div className="col" style={{ textAlign: "center" }}>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleCloseUnsavedChangesModal}
              >
                {t("Cancel")}
              </button>
            </div>
            <div className="col" style={{ textAlign: "center" }}>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleConfirmUnsavedChanges}
              >
                {t("Confirm")}
              </button>
            </div>
            <div className="col"></div>
          </div>
        </Modal.Body>
      </Modal>
      <div className="row mb-3 g-2 align-items-center">
        <div className="col-md-5">
          <input
            className="form-control form-control-sm"
            type="text"
            placeholder={t("SearchOrganizations")}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.currentTarget.value)}
          />
        </div>
        <div className="col-md-auto">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={reloadData}
            disabled={isSaving}
          >
            {t("ReloadWhitelists")}
          </button>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-4">
          <div
            className="list-group"
            style={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            {manageableOrganizations.map((organization) => {
              const companyPolicy =
                selectedOrganizationId === organization.organizationId &&
                editorPolicy.organizationId === organization.organizationId
                  ? editorPolicy
                  : policiesByOrganizationId[organization.organizationId] ||
                    createDefaultPolicy(organization.organizationId);
              const summary = [];

              if (companyPolicy.buyFromAll) {
                summary.push(t("BuyFromAll"));
              }
              if (companyPolicy.sellToAll) {
                summary.push(t("SellToAll"));
              }
              if (
                summary.length === 0 &&
                companyPolicy.counterparties.length > 0
              ) {
                summary.push(
                  t("SpecificCounterparties", {
                    count: companyPolicy.counterparties.length,
                  })
                );
              }
              if (summary.length === 0) {
                summary.push(t("NoCounterpartiesConfigured"));
              }

              return (
                <button
                  type="button"
                  key={organization.organizationId}
                  className={`list-group-item list-group-item-action${
                    selectedOrganizationId === organization.organizationId
                      ? " active"
                      : ""
                  }`}
                  onClick={() =>
                    handleSelectOrganization(organization.organizationId)
                  }
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span>{organization.name || organization.organizationId}</span>
                    {selectedOrganizationId === organization.organizationId &&
                      hasUnsavedChanges && (
                        <span className="badge bg-warning text-dark">
                          {t("Draft")}
                        </span>
                      )}
                  </div>
                  <small
                    className={
                      selectedOrganizationId === organization.organizationId
                        ? "text-white"
                        : "text-muted"
                    }
                  >
                    {summary.join(" | ")}
                  </small>
                </button>
              );
            })}
            {manageableOrganizations.length === 0 && (
              <div className="list-group-item text-muted">
                {t("NoWhitelistsAvailable")}
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-8">
          {selectedOrganizationId ? (
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="card-title mb-1">
                      {organizationCatalog.find(
                        (organization) =>
                          organization.organizationId === selectedOrganizationId
                      )?.name || selectedOrganizationId}
                    </h5>
                    <p className="text-muted mb-0">{selectedOrganizationId}</p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    disabled={!allowEdit || isSaving || !hasUnsavedChanges}
                    onClick={handleSaveCurrent}
                  >
                    {t("Save")}
                  </button>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="buyFromAll"
                        checked={editorPolicy.buyFromAll}
                        disabled={!allowEdit}
                        onChange={(event) =>
                          updateEditorPolicy({
                            buyFromAll: event.currentTarget.checked,
                          })
                        }
                      />
                      <label className="form-check-label" htmlFor="buyFromAll">
                        {t("BuyFromAll")}
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="sellToAll"
                        checked={editorPolicy.sellToAll}
                        disabled={!allowEdit}
                        onChange={(event) =>
                          updateEditorPolicy({
                            sellToAll: event.currentTarget.checked,
                          })
                        }
                      />
                      <label className="form-check-label" htmlFor="sellToAll">
                        {t("SellToAll")}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="row g-4">
                  <div className="col-xl-6">
                    <label className="form-label">{t("ApprovedToBuyFrom")}</label>
                    <DualListBox
                      canFilter
                      className="rdl-hide-move-all"
                      disabled={!allowEdit}
                      options={counterpartyOptions}
                      selected={currentBuyFromSelection}
                      onChange={(selected) =>
                        updateCounterpartyDirection("buy", selected)
                      }
                    />
                  </div>
                  <div className="col-xl-6">
                    <label className="form-label">{t("ApprovedToSellTo")}</label>
                    <DualListBox
                      canFilter
                      className="rdl-hide-move-all"
                      disabled={!allowEdit}
                      options={counterpartyOptions}
                      selected={currentSellToSelection}
                      onChange={(selected) =>
                        updateCounterpartyDirection("sell", selected)
                      }
                    />
                  </div>
                </div>

                <p className="text-muted mt-3 mb-0">
                  {t("CompanyWhitelistHelpText")}
                </p>
              </div>
            </div>
          ) : (
            <div className="alert alert-secondary mb-0">
              {t("SelectOrganizationToManageWhitelist")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyWhitelistManager;
